import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCalendarPlus, LuInfo, LuLoaderCircle, LuPlus, LuSave } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import IconButton from '../../../components/ui/IconButton';
import Modal from '../../../components/ui/Modal';
import { Field, SelectInput, TextInput, ToggleTile } from '../../../components/form/FormControls';
import { db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { createVisitMirrors, findMirrorTargets, isMirrorVisit } from '../../../utils/hybridVisitMirror';
import { fetchAccessiblePatients, getVisitedPatientIds, normalizePatientId } from '../../../utils/patientAccess';
import AddPatientModal from '../../ProjectsManagement/AddPatientModal';
import { patientStatusLabel } from '../../ProjectsManagement/patients/patientStatuses';
import { BLOCK_TITLE, HelpNote } from '../components/ProfileUi';
import { dateKey } from './calendarDates';

const DAY_MS = 1000 * 60 * 60 * 24;
const VISIT_TYPES = ['hastane', 'home', 'telefon', 'unplanning', 'handover'];
const WINDOW_PRESETS = [0, 1, 3, 7];

const shiftDay = (value, days) => {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return dateKey(date);
};

/**
 * "Yeni Visit Ekle": a visit (or a repeating series) for one of the user's
 * projects. Hybrid projects copy the visit to the unblinded colleague's
 * calendar and e-mail them; the visit can also be sent to Google Calendar.
 */
const AddVisitDialog = ({ isOpen, initialDate, employeeId, employeeData, userProjects, visits, onClose, onCreated }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.visitForm.${key}`, vars);

  const [selectedProject, setSelectedProject] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [visitPeriodBefore, setVisitPeriodBefore] = useState(''); // Pencere başlangıcı
  const [visitPeriodAfter, setVisitPeriodAfter] = useState(''); // Pencere bitişi
  const [patientId, setPatientId] = useState('');
  const [visitType, setVisitType] = useState('');
  const [visitNumber, setVisitNumber] = useState('');
  // Proje + hastaya göre sıradaki vizit numarası; null: henüz hesaplanmadı
  const [suggestedVisitNumber, setSuggestedVisitNumber] = useState(null);
  const [visitInfo, setVisitInfo] = useState('');
  const [visitRepeatPeriod, setVisitRepeatPeriod] = useState('');
  const [visitRepeatUnit, setVisitRepeatUnit] = useState('week');
  const [visitEndDate, setVisitEndDate] = useState('');
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [willRepeat, setWillRepeat] = useState(false);
  const [screenFail, setScreenFail] = useState(false);
  const [projectPatients, setProjectPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Her açılışta boş form (seçili gün varsa ilk ziyaret tarihi o gün)
  useEffect(() => {
    if (!isOpen) return;
    setSelectedProject('');
    setVisitDate(initialDate ? dateKey(initialDate) : '');
    setVisitPeriodBefore('');
    setVisitPeriodAfter('');
    setPatientId('');
    setVisitType('');
    setVisitNumber('');
    setVisitInfo('');
    setVisitRepeatPeriod('');
    setVisitRepeatUnit('week');
    setVisitEndDate('');
    setAddToCalendar(false);
    setWillRepeat(false);
    setScreenFail(false);
  }, [isOpen, initialDate]);

  /**
   * Kaçıncı vizit olduğu: aynı hastanın aynı projedeki en yüksek vizit
   * numarasının bir fazlası (hiç yoksa 1). Alan serbesttir; proje veya hasta
   * değişince öneri yeniden hesaplanır. Hasta eşleşmesi normalizePatientId
   * ile yapılır (boşluk / büyük-küçük harf farkları kaçmasın).
   */
  useEffect(() => {
    const normalizedPatient = normalizePatientId(patientId);
    if (!isOpen || !selectedProject || !normalizedPatient) {
      setSuggestedVisitNumber(null);
      return undefined;
    }
    let cancelled = false;
    const suggestVisitNumber = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, 'Visits'), where('projectId', '==', selectedProject)));
        if (cancelled) return;
        const numbers = snapshot.docs
          .map((d) => d.data())
          .filter((v) => !isMirrorVisit(v))
          .filter((v) => normalizePatientId(v.patientId) === normalizedPatient)
          .map((v) => Number(v.visitNumber))
          .filter((n) => Number.isFinite(n) && n > 0);
        const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
        setSuggestedVisitNumber(next);
        setVisitNumber(String(next));
      } catch (error) {
        console.error('Vizit numarası önerilemedi:', error);
        if (!cancelled) setSuggestedVisitNumber(null);
      }
    };
    suggestVisitNumber();
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedProject, patientId]);

  // Kullanıcının bu projede vizit açtığı hastalar (takvimdeki vizitlerinden)
  const visitedPatientIds = useMemo(
    () => getVisitedPatientIds(visits, selectedProject, employeeId),
    [visits, selectedProject, employeeId]
  );

  // Seçilebilecek hastalar: vizit açtıkları + kendi ekledikleri
  const fetchProjectPatients = useCallback(async (projectId, ownVisitedPatientIds) => {
    if (!projectId || !employeeId) {
      setProjectPatients([]);
      return;
    }
    setLoadingPatients(true);
    try {
      setProjectPatients(await fetchAccessiblePatients(projectId, employeeId, { visitedPatientIds: ownVisitedPatientIds }));
    } catch (error) {
      console.error('Error fetching patients:', error);
      setProjectPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (selectedProject) fetchProjectPatients(selectedProject, visitedPatientIds);
    else setProjectPatients([]);
  }, [selectedProject, visitedPatientIds, fetchProjectPatients]);

  useEffect(() => {
    setPatientId('');
  }, [selectedProject]);

  const handlePatientAdded = (savedPatient) => {
    setShowAddPatientModal(false);
    // Yeni eklenen hasta kullanıcınındır: hemen listelenip seçilir
    if (savedPatient?.patientId) {
      setProjectPatients((prev) => (prev.some((patient) => patient.id === savedPatient.id)
        ? prev
        : [...prev, savedPatient].sort((a, b) => (a.patientId || '').localeCompare(b.patientId || ''))));
      setPatientId(savedPatient.patientId);
    }
    if (selectedProject) fetchProjectPatients(selectedProject, visitedPatientIds);
  };

  const projectNameOf = (projectId) => {
    const project = userProjects.find((p) => p.id === projectId);
    return project ? (project.titleShort || project.title) : 'Ziyaret';
  };

  // Google Takvim'de tüm gün etkinliği (bitiş günü hariç tutulur)
  const createGoogleCalendarUrl = (projectName) => {
    try {
      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      endDate.setDate(endDate.getDate() + 1);
      const formatDateForGCal = (date) => `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
      const eventTitle = encodeURIComponent(`Ziyaret: ${projectName}`);
      const eventDetails = encodeURIComponent(`Hasta ID: ${patientId || 'Belirtilmemiş'}\n${visitInfo || ''}`);
      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&dates=${formatDateForGCal(startDate)}/${formatDateForGCal(endDate)}&details=${eventDetails}&location=&sf=true&output=xml`;
    } catch (error) {
      console.error('Error creating calendar URL:', error);
      return '';
    }
  };

  // Eş vizit açılan çalışanlara e-posta (proje tarafındaki vizit atamasıyla aynı fonksiyon)
  const notifyMirrorTargets = (targets, project, visitData, periodString) => {
    targets.forEach((target) => {
      fetch('https://us-central1-habcare-8ecad.cloudfunctions.net/sendEmployeeVisitNotification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: target.id,
          visitDate: visitData.firstVisitDate,
          projectName: project.titleShort || 'Not specified',
          period: periodString,
          patientId: visitData.patientId || '',
          info: visitData.info || '',
        }),
      }).catch((notifyError) => console.error('Eş vizit bildirimi gönderilemedi:', notifyError));
    });
  };

  const handleVisitSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!visitPeriodBefore || !visitPeriodAfter || !visitDate) {
      toast.error(label('errors.dates'));
      return;
    }

    const startDate = new Date(visitPeriodBefore);
    const endDate = new Date(visitPeriodAfter);
    const firstVisit = new Date(visitDate);
    const daysBefore = Math.ceil((firstVisit - startDate) / DAY_MS);
    const daysAfter = Math.ceil((endDate - firstVisit) / DAY_MS);

    if (startDate > firstVisit || endDate < firstVisit) {
      toast.error(label('errors.window'));
      return;
    }

    const periodString = daysBefore > 0 || daysAfter > 0 ? `-${daysBefore},+${daysAfter}` : '0';
    const totalDays = daysBefore + daysAfter;

    const baseVisitData = {
      projectId: selectedProject,
      employeeId,
      period: periodString, // "-2,+3"
      option: Number(''),
      patientId: normalizePatientId(patientId),
      visitType: visitType || '',
      visitNumber: visitNumber ? Number(visitNumber) : '',
      info: visitInfo,
      createdAt: new Date(),
      preVisitForm: false,
      screenFail: visitType === 'hastane' && screenFail ? true : false,
    };

    const startDateTime = new Date(visitDate);
    const visitPromises = [];

    if (willRepeat) {
      const endDateTime = new Date(visitEndDate);
      const repeatPeriod = Number(visitRepeatPeriod);
      // Periyot 0/geçersizse tarih ilerlemez ve döngü bitmez
      if (!Number.isFinite(repeatPeriod) || repeatPeriod <= 0) {
        toast.error(label('errors.repeatPeriod'));
        return;
      }
      // Tekrarlar sıradaki vizitlerdir: numara her tekrarda bir artar
      const startNumber = visitNumber ? Number(visitNumber) : null;
      let repeatIndex = 0;
      let currentVisitDate = new Date(startDateTime);
      while (currentVisitDate <= endDateTime) {
        const visitData = {
          ...baseVisitData,
          visitNumber: startNumber !== null ? startNumber + repeatIndex : '',
          firstVisitDate: currentVisitDate.toISOString().split('T')[0],
        };
        visitPromises.push(addDoc(collection(db, 'Visits'), visitData).then((ref) => ({ id: ref.id, data: visitData })));
        repeatIndex += 1;

        currentVisitDate = new Date(currentVisitDate);
        if (visitRepeatUnit === 'week') {
          currentVisitDate.setDate(currentVisitDate.getDate() + (7 * repeatPeriod));
        } else if (visitRepeatUnit === 'month') {
          currentVisitDate.setMonth(currentVisitDate.getMonth() + repeatPeriod);
        } else {
          // Gün: periyot + pencere süresi kadar ilerlenir
          currentVisitDate.setDate(currentVisitDate.getDate() + repeatPeriod + totalDays);
        }
      }
    } else {
      const visitData = { ...baseVisitData, firstVisitDate: startDateTime.toISOString().split('T')[0] };
      visitPromises.push(addDoc(collection(db, 'Visits'), visitData).then((ref) => ({ id: ref.id, data: visitData })));
    }

    setSaving(true);
    try {
      const createdVisits = await Promise.all(visitPromises);

      // Hibrit proje + primer/blinded çalışan: vizit aynı projedeki unblinded
      // çalışanın takvimine de düşer. Eşleme başarısız olsa da kendi viziti kayıtlıdır.
      const visitProject = userProjects.find((p) => p.id === selectedProject);
      const mirrorTargets = findMirrorTargets(visitProject, employeeId);
      if (mirrorTargets.length > 0) {
        try {
          const sourceName = `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim();
          const result = await createVisitMirrors({
            project: visitProject,
            source: { id: employeeId, name: sourceName },
            createdVisits,
            targets: mirrorTargets,
          });
          const names = mirrorTargets.map((target) => target.name).filter(Boolean).join(', ');
          if (result.created > 0) {
            toast.info(names ? label('mirror.createdNamed', { names }) : label('mirror.created'));
            notifyMirrorTargets(mirrorTargets, visitProject, createdVisits[0].data, periodString);
          } else if (result.skipped > 0) {
            toast.info(label('mirror.skipped'));
          }
        } catch (mirrorError) {
          console.error('Hibrit vizit eşlemesi başarısız:', mirrorError);
          toast.warning(label('mirror.failed'));
        }
      }

      if (addToCalendar) {
        const calendarUrl = createGoogleCalendarUrl(projectNameOf(selectedProject));
        if (calendarUrl) window.open(calendarUrl, '_blank');
      }

      toast.success(label('saved', { count: createdVisits.length }));
      onClose();
      await onCreated();
    } catch (error) {
      console.error('Error adding visits:', error);
      toast.error(label('errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const setWindow = (days) => {
    if (!visitDate) return;
    setVisitPeriodBefore(shiftDay(visitDate, -days));
    setVisitPeriodAfter(shiftDay(visitDate, days));
  };

  const windowSummary = () => {
    if (!visitDate || !visitPeriodBefore || !visitPeriodAfter) return null;
    const before = Math.ceil((new Date(visitDate) - new Date(visitPeriodBefore)) / DAY_MS);
    const after = Math.ceil((new Date(visitPeriodAfter) - new Date(visitDate)) / DAY_MS);
    const total = Math.ceil((new Date(visitPeriodAfter) - new Date(visitPeriodBefore)) / DAY_MS);
    return { before, after, total };
  };
  const summary = windowSummary();
  const windowInvalid = Boolean(summary) && (summary.before < 0 || summary.after < 0);

  let patientControl;
  if (!selectedProject) {
    patientControl = <SelectInput id="visit-patient" disabled value="" options={[]} placeholder={label('chooseProjectFirst')} />;
  } else if (loadingPatients) {
    patientControl = <SelectInput id="visit-patient" disabled value="" options={[]} placeholder={label('patientsLoading')} />;
  } else if (projectPatients.length === 0) {
    patientControl = <SelectInput id="visit-patient" disabled value="" options={[]} placeholder={label('noPatients')} />;
  } else {
    patientControl = (
      <SelectInput
        id="visit-patient"
        value={patientId}
        onChange={(event) => setPatientId(event.target.value)}
        placeholder={label('patientOptional')}
        options={projectPatients.map((patient) => ({
          value: patient.patientId,
          label: patient.status ? `${patient.patientId} (${patientStatusLabel(t, patient.status)})` : patient.patientId,
        }))}
      />
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => !saving && onClose()}
        size="form"
        title={label('title')}
        subtitle={label('subtitle')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
            <LuCalendarPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving} className="max-sm:tw-hidden">{t('profilePage.common.cancel')}</Button>
            <Button variant="primary" type="submit" form="profile-add-visit" disabled={saving || windowInvalid} className="max-sm:tw-flex-1">
              {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {saving ? label('saving') : t('profilePage.common.save')}
            </Button>
          </>
        )}
      >
        <form id="profile-add-visit" onSubmit={handleVisitSubmit} className="tw-space-y-6">
          {/* Proje ve hasta */}
          <fieldset className="tw-space-y-3">
            <legend className={BLOCK_TITLE}>{label('sections.who')}</legend>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label={label('project')} htmlFor="visit-project" required>
                <SelectInput
                  id="visit-project"
                  required
                  value={selectedProject}
                  onChange={(event) => setSelectedProject(event.target.value)}
                  placeholder={label('chooseProject')}
                  options={userProjects.map((project) => ({ value: project.id, label: project.titleShort || project.title || project.id }))}
                />
              </Field>
              <Field label={label('patient')} htmlFor="visit-patient" hint={selectedProject && !loadingPatients && projectPatients.length === 0 ? label('noPatientsHint') : label('patientHint')}>
                <div className="tw-flex tw-gap-2">
                  <div className="tw-min-w-0 tw-flex-1">{patientControl}</div>
                  <IconButton
                    label={label('addPatient')}
                    tone="accent"
                    onClick={() => setShowAddPatientModal(true)}
                    disabled={!selectedProject}
                    className="disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                  >
                    <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                </div>
              </Field>
            </div>
          </fieldset>

          {/* Tarih ve pencere */}
          <fieldset className="tw-space-y-3">
            <legend className={BLOCK_TITLE}>{label('sections.when')}</legend>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
              <Field label={label('visitDate')} htmlFor="visit-date" required hint={label('visitDateHint')}>
                <TextInput id="visit-date" type="date" required value={visitDate} onChange={(event) => setVisitDate(event.target.value)} />
              </Field>
              <Field label={label('windowStart')} htmlFor="visit-window-start" required>
                <TextInput id="visit-window-start" type="date" required max={visitDate || undefined} value={visitPeriodBefore} onChange={(event) => setVisitPeriodBefore(event.target.value)} />
              </Field>
              <Field label={label('windowEnd')} htmlFor="visit-window-end" required>
                <TextInput id="visit-window-end" type="date" required min={visitDate || undefined} value={visitPeriodAfter} onChange={(event) => setVisitPeriodAfter(event.target.value)} />
              </Field>
            </div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
              <span className="tw-mr-1 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('quickWindow')}</span>
              {WINDOW_PRESETS.map((days) => (
                <button
                  key={days}
                  type="button"
                  disabled={!visitDate}
                  onClick={() => setWindow(days)}
                  className="tw-h-8 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-text-xs tw-font-semibold tw-text-slate-700 tw-transition-colors hover:tw-border-teal-400/50 hover:tw-text-teal-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-200"
                >
                  {days === 0 ? label('sameDay') : label('plusMinus', { n: days })}
                </button>
              ))}
            </div>
            {summary && (
              <div className={`tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-px-3.5 tw-py-3 tw-text-sm ${windowInvalid ? 'tw-bg-rose-500/10 tw-text-rose-800 dark:tw-text-rose-200' : 'tw-bg-teal-500/[0.08] tw-text-teal-900 dark:tw-text-teal-100'}`}>
                <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
                <div className="tw-min-w-0">
                  <p className="tw-font-semibold">
                    {new Date(visitDate).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <p className="tw-text-xs tw-opacity-80">
                    {windowInvalid ? label('errors.window') : label('windowSummary', summary)}
                  </p>
                </div>
              </div>
            )}
          </fieldset>

          {/* Vizit bilgileri */}
          <fieldset className="tw-space-y-3">
            <legend className={BLOCK_TITLE}>{label('sections.details')}</legend>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label={label('visitType')} htmlFor="visit-type">
                <SelectInput
                  id="visit-type"
                  value={visitType}
                  onChange={(event) => setVisitType(event.target.value)}
                  placeholder={label('choose')}
                  options={VISIT_TYPES.map((type) => ({ value: type, label: t(`profilePage.calendar.visitTypes.${type}`) }))}
                />
              </Field>
              <Field
                label={label('visitNumber')}
                htmlFor="visit-number"
                hint={suggestedVisitNumber !== null
                  ? (String(suggestedVisitNumber) === String(visitNumber) ? label('suggestedNext', { n: suggestedVisitNumber }) : label('suggested', { n: suggestedVisitNumber }))
                  : label('visitNumberHint')}
              >
                <TextInput id="visit-number" type="number" min="1" inputMode="numeric" value={visitNumber} onChange={(event) => setVisitNumber(event.target.value)} placeholder={label('visitNumberPlaceholder')} />
              </Field>
              {visitType === 'hastane' && (
                <div className="tw-space-y-1.5 sm:tw-col-span-2">
                  <ToggleTile id="visit-screen-fail" label={label('screenFail')} checked={screenFail} onChange={setScreenFail} />
                  {screenFail && <p className="tw-px-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('screenFailNote')}</p>}
                </div>
              )}
              <Field label={label('info')} htmlFor="visit-info" className="sm:tw-col-span-2">
                <TextInput id="visit-info" value={visitInfo} onChange={(event) => setVisitInfo(event.target.value)} placeholder={label('infoPlaceholder')} />
              </Field>
            </div>
          </fieldset>

          {/* Seçenekler */}
          <fieldset className="tw-space-y-3">
            <legend className={BLOCK_TITLE}>{label('sections.options')}</legend>
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              <ToggleTile id="visit-repeat" label={label('repeat')} checked={willRepeat} onChange={setWillRepeat} />
              <ToggleTile id="visit-google" label={label('googleCalendar')} checked={addToCalendar} onChange={setAddToCalendar} />
            </div>
            {willRepeat && (
              <div className="tw-grid tw-gap-4 tw-rounded-2xl tw-border tw-border-slate-200 tw-p-3.5 sm:tw-grid-cols-2 dark:tw-border-white/10">
                <Field label={label('repeatEnd')} htmlFor="visit-repeat-end" required hint={label('repeatEndHint')}>
                  <TextInput id="visit-repeat-end" type="date" required min={visitDate || undefined} value={visitEndDate} onChange={(event) => setVisitEndDate(event.target.value)} />
                </Field>
                <Field label={label('repeatEvery')} htmlFor="visit-repeat-period" required hint={visitRepeatUnit === 'day' ? label('repeatDayHint') : undefined}>
                  <div className="tw-flex tw-gap-2">
                    <TextInput id="visit-repeat-period" type="number" min="1" inputMode="numeric" required value={visitRepeatPeriod} onChange={(event) => setVisitRepeatPeriod(event.target.value)} className="tw-flex-[2]" />
                    <div className="tw-flex-1">
                      <SelectInput
                        aria-label={label('repeatUnit')}
                        value={visitRepeatUnit}
                        onChange={(event) => setVisitRepeatUnit(event.target.value)}
                        options={['day', 'week', 'month'].map((unit) => ({ value: unit, label: label(`units.${unit}`) }))}
                      />
                    </div>
                  </div>
                </Field>
              </div>
            )}
            {addToCalendar && <HelpNote>{label('googleHint')}</HelpNote>}
          </fieldset>
        </form>
      </Modal>

      {showAddPatientModal && selectedProject && (
        <AddPatientModal
          project={{ id: selectedProject }}
          onClose={() => setShowAddPatientModal(false)}
          onSave={handlePatientAdded}
        />
      )}
    </>
  );
};

AddVisitDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialDate: PropTypes.instanceOf(Date),
  employeeId: PropTypes.string,
  employeeData: PropTypes.object,
  userProjects: PropTypes.arrayOf(PropTypes.object).isRequired,
  visits: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default AddVisitDialog;
